import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from '../../../styles/EducationHub.module.css';
import { useEducationHub } from '../../../contexts/EducationHubContext';
import LoadingSpinner from 'components/common/LoadingSpinner';

const NewsSection: React.FC = () => {
  const { news: initialNews, newsLoading: loading, newsCategories: categories } = useEducationHub();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  
  // Get filtered news
  const filteredNews = initialNews.filter(newsItem => {
    if (selectedCategory && newsItem.category !== selectedCategory) return false;
    if (selectedPriority && newsItem.priority !== selectedPriority) return false;
    return true;
  });

  const priorities = ['Critical', 'High', 'Medium', 'Low'];

  const handleNewsClick = (newsItem: any) => {
    setSearchParams({ type: 'news', id: newsItem._id });
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderNewsCard = (newsItem: any) => {
    return (
      <div
        key={newsItem._id}
        className={`${styles.contentCard} ${styles.clickableContentCard}`}
        onClick={() => handleNewsClick(newsItem)}
      >
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>
            {newsItem.featured && <span style={{ marginRight: '8px' }}>📌</span>}
            {newsItem.title}
          </h3>
          <div className={`${styles.priorityBadge} ${styles[`priority${newsItem.priority}`]}`}>
            {newsItem.priority}
          </div>
        </div>
        
        <p className={styles.cardExcerpt}>{newsItem.excerpt}</p>
        
        <div className={styles.cardMeta}>
          <div className={styles.cardDate}>
            <span>📂 {newsItem.category}</span>
            <span>📅 {formatDate(newsItem.publishedAt || newsItem.createdAt)}</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {newsItem.featured && <span title="Featured">⭐</span>}
            <span>👁️ {newsItem.viewCount}</span>
          </div>
        </div>
        
        {newsItem.expiresAt && new Date(newsItem.expiresAt) > new Date() && (
          <div style={{ 
            marginTop: '8px', 
            fontSize: '12px', 
            color: '#d97706',
            fontWeight: '500'
          }}>
            ⏰ Expires: {formatDate(newsItem.expiresAt)}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className={styles.filterContainer}>
        <div className={styles.categoryFilterWrapper}>
          <label className={styles.categoryLabel}>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className={styles.categorySelect}
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className={styles.categoryFilterWrapper}>
          <label className={styles.categoryLabel}>Priority:</label>
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className={styles.categorySelect}
          >
            <option value="">All Priorities</option>
            {priorities.map(priority => (
              <option key={priority} value={priority}>{priority}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterResultCount}>
          {filteredNews.length} news post{filteredNews.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Featured News Banner */}
      {!selectedCategory && !selectedPriority && (
        (() => {
          const featuredNews = initialNews.filter(n => n.featured).slice(0, 1);
          return featuredNews.length > 0 && (
            <div
              className={`${styles.featuredBanner} ${styles.featuredBannerNews}`}
              onClick={() => handleNewsClick(featuredNews[0])}
            >
              <div className={styles.featuredBannerHeader}>
                <span>📌</span>
                <span>Featured News</span>
              </div>
              <h3 className={styles.featuredBannerTitle}>
                {featuredNews[0].title}
              </h3>
              <p className={styles.featuredBannerExcerpt}>
                {featuredNews[0].excerpt}
              </p>
            </div>
          );
        })()
      )}

      {/* News Grid */}
      {filteredNews.length > 0 ? (
        <div className={styles.contentGrid}>
          {filteredNews
            .filter(n => !n.featured || selectedCategory || selectedPriority) // Hide featured from grid if shown in banner
            .map(renderNewsCard)}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📰</div>
          <h3 className={styles.emptyTitle}>
            {initialNews.length === 0 ? "Stay Tuned for Market News!" : "No News Matches Your Filters"}
          </h3>
          <p className={styles.emptyDescription}>
            {initialNews.length === 0 
              ? "The latest market news and announcements will appear here once published by the platform team. Keep checking back for industry insights and updates!" 
              : "No news posts match your current filters. Try adjusting your search criteria to find relevant content."
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default NewsSection;
